/**
 * Lazily loads the Google Maps JavaScript API (with the Places library) exactly
 * once and resolves with the `google.maps.places` library namespace.
 *
 * Loads with `loading=async` and waits for Google's `callback` (the reliable
 * readiness signal), then resolves the places library via `importLibrary` when
 * available, falling back to the eagerly loaded `google.maps.places` namespace.
 * Targets the Places API (New) surface, matching the referrer-restricted
 * frontend key provisioned by terraform/google-maps (Maps JS + Places API New).
 *
 * Returns a rejected promise if the key is missing or the script fails to load,
 * so callers can degrade gracefully to a plain text input.
 */

import { googleMapsApiKey } from '../config/googleMaps';

let loadPromise = null;

// Only this exact origin may ever be turned into a TrustedScriptURL by the
// policy below. The Maps loader builds its URL from a compile-time constant, so
// anything else means the call site was changed (or the policy got reused) and
// should hard-fail rather than silently pass through.
const ALLOWED_SCRIPT_ORIGIN = 'https://maps.googleapis.com/';

// Trusted Types support: some environments (enterprise-managed Chrome, certain
// security extensions) enforce `require-trusted-types-for 'script'`, which
// forbids assigning a plain string to `script.src`. When the API is present we
// register an origin-validating policy so the Maps loader keeps working;
// otherwise we fall back to the plain string. `undefined` marks "TT present but
// policy creation was blocked by CSP", so we don't retry it.
let scriptUrlPolicy = null;

const getScriptUrlPolicy = () => {
  if (scriptUrlPolicy !== null) return scriptUrlPolicy;

  if (window.trustedTypes && window.trustedTypes.createPolicy) {
    try {
      scriptUrlPolicy = window.trustedTypes.createPolicy('zenrent-gmaps-loader', {
        createScriptURL: (url) => {
          if (!url.startsWith(ALLOWED_SCRIPT_ORIGIN)) {
            throw new Error('Google Maps loader: refusing to trust unexpected script URL');
          }
          return url;
        },
      });
    } catch (err) {
      // CSP `trusted-types` directive disallowed creating this policy.
      console.error('Google Maps loader: Trusted Types policy creation blocked', err);
      scriptUrlPolicy = undefined;
    }
  } else {
    scriptUrlPolicy = undefined;
  }

  return scriptUrlPolicy;
};

const toScriptSrc = (url) => {
  const policy = getScriptUrlPolicy();
  return policy ? policy.createScriptURL(url) : url;
};


const MAPS_READY_CALLBACK = '__zenrentGoogleMapsReady__';

const injectScript = () =>
  new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }

    const finish = () => resolve(window.google);

    const existing = document.getElementById('google-maps-js');
    if (existing) {
      window[MAPS_READY_CALLBACK] = finish;
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Maps JS API'))
      );
      return;
    }

    window[MAPS_READY_CALLBACK] = finish;

    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.async = true;
    script.defer = true;
    const url =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(googleMapsApiKey)}` +
      `&libraries=places&v=weekly&loading=async&callback=${MAPS_READY_CALLBACK}`;
    script.onerror = () => reject(new Error('Failed to load Google Maps JS API'));
    // toScriptSrc returns a TrustedScriptURL when Trusted Types is enforced.
    script.src = toScriptSrc(url);
    document.head.appendChild(script);
  });

/**
 * @returns {Promise<google.maps.PlacesLibrary>} the Places library namespace.
 */
export function loadPlacesLibrary() {
  if (loadPromise) return loadPromise;

  if (!googleMapsApiKey) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set'));
  }

  loadPromise = injectScript()
    .then((google) => {
      // After the callback fires the API is fully initialized. Prefer the
      // dynamic import when present; fall back to the eagerly loaded namespace
      // (we request `libraries=places`) so we never depend on importLibrary.
      if (typeof google.maps.importLibrary === 'function') {
        return google.maps.importLibrary('places');
      }
      if (google.maps.places) return google.maps.places;
      throw new Error('Google Maps Places library unavailable');
    })
    .catch((err) => {
      console.error('Google Maps JS API failed to load', err);
      // Tear down so a later attempt retries cleanly. Leaving the stale
      // <script> behind makes injectScript take the "existing element" branch
      // on retry, where neither the (already-fired, non-replaying) error event
      // nor the callback can ever fire again, leaving the promise pending
      // forever. Also drop the global readiness callback so the next injection
      // starts from a clean slate.
      document.getElementById('google-maps-js')?.remove();
      delete window[MAPS_READY_CALLBACK];
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}
